import { IRowData } from "@multiweb/types/contracts";
import { IDataSourceExtended, IEditableDataSource } from "../../../Global/Base/DataSources/Interfaces";
import { NotImplementedException } from "../../../Global/Base/Exceptions";
import { CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
import { OWObject } from "../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IExtendedObjectOperation } from "../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { AggregatedDataSource } from "./AggregatedDataSource";
import { INewRow, SaveResultData } from "./EditableDBDataSource";
import { IObjectDataSource } from "./IObjectDataSource";

export type IObjectExtendedDataSource = IDataSourceExtended & IEditableDataSource & IObjectDataSource;

/**
 * Trida reprezentujici agregovany databazovy zdroj dat pro konzumenta dat
 */
export class AggregatedObjectDataSource<T extends IObjectExtendedDataSource> extends AggregatedDataSource<T> {
    /**
     * Vrací počet editovaných objektů.
     */
    public get editedObjectsCount(): number {
        return this._dataSources.map(ds => ds.editedObjectsCount).reduce((prev, curr) => prev + curr);
    }

    /**
     * Vytvori nový řádek
     * @param objectName Název třídy, jejíž záznam se má založit
     * @param extendedOperations Operace, které se mají provést ihned po založení nové instance aplikačního záznamu
     * @param attrNames Seznam atributů, které se mají z nového záznamu přečíst
     */
    public async newRow(objectName: string, extendedOperations?: IExtendedObjectOperation[], attrNames?: string[]): Promise<INewRow> {
        const ds = this._dataSources.find(ds => ds.className === objectName);
        if (!ds)
            return null;

        return ds.newRow(extendedOperations, objectName, attrNames);
    }

    /**
     * Uloží změněné objekty
     * @param saveOnlyKeys Pokud je vyplněno, uloží jen objekty v této kolekci
     */
    public async saveObjects(saveOnlyKeys?: string[]): Promise<SaveResultData[]> {
        let result: SaveResultData[] = [];
        for (const ds of this._dataSources) {
            if (ds.editedObjectsCount === 0)
                continue;

            result = result.concat(await ds.saveObjects(saveOnlyKeys));
        }
        return result;
    }

    /**
     * Metoda pro dočtení jednoho určitého záznamu
     * @param objectName Název třídy, jejíž záznamy se mají dočíst
     * @param dataKey Datový klíč (většinou ID) k načtení
     * @param selectLoaded Příznak, zda se dočtené záznamy mají označit
     */
    public async loadSpecificObjectRecord(objectName: string, dataKey: string, selectLoaded: boolean, cancellationToken?: CancellationToken): Promise<void> {
        const loading: Promise<any>[] = [];
        for (const ds of this._dataSources) {
            if (ds.className !== objectName)
                continue;

            loading.push(ds.loadSpecificRecord(dataKey, selectLoaded, cancellationToken));
        }

        await Promise.all(loading);
    }

    /**
     * Vrací objekt z konkrétního datového zdroje.
     * @param uniqueId Unikátní id datového zdroje.
     * @param dataKey Klíč záznamu.
     */
    public getObject(uniqueId: string, dataKey: string, forceLoad?: boolean): Promise<OWObject | undefined> {
        const dsData = this._dataSourcesByClassName.get(uniqueId);
        return dsData && dsData.ds ? dsData.ds.getObject(dataKey, null, forceLoad) : Promise.resolve(undefined);
    }

    /**
     * Smaže objekt ze všech registrovaných datových zdrojů.
     * @param objectName Název objektu.
     * @param dataKey Klíč objektu.
     */
    public async deleteObject(objectName: string, dataKey: string): Promise<void> {
        const loading: Promise<any>[] = [];
        for (const ds of this._dataSources) {
            if (ds.className !== objectName)
                continue;

            loading.push(ds.deleteObject(dataKey));
        }

        await Promise.all(loading);
    }

    /**
     * Zruseni provedenych zmen
     * @param key - ID zaznamu, na kterem rusime zmeny
     * @param forceRefreshCancelledRecord - Znovu načte záznam, na kterém byly zrušeny změny
     */
    public async cancelChanges(key?: string, forceRefreshCancelledRecord?: boolean): Promise<void> {
        await Promise.all(this._dataSources.map(ds => ds.cancelChanges(key, forceRefreshCancelledRecord)));
    }

    public override setFilterCondition(_condition: string): void {
        throw new NotImplementedException();
    }
    /**
     * Vrací true, pokud je datasource filtrován.
     */
    public override get hasFilterCondition(): boolean {
        return false;
    }

    /**
     * Metoda pro dočtení jednoho určitého záznamu
     * @param _dataKey Datový klíč (většinou ID) k načtení
     * @param _selectLoaded Příznak, zda se dočtené záznamy mají označit
     */
    public override async loadSpecificRecord(_dataKey: string, _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData> {
        throw new NotImplementedException();
    }

    /**
    * Metoda pro dočtení určitých záznamů
    * @param _dataKeys Pole datových klíčů (většinou ID) k načtení
    * @param _selectLoaded Příznak, zda se dočtené záznamy mají označit
    */
    public override async loadSpecificRecords(_dataKeys: string[], _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        throw new NotImplementedException();
    }
    //#endregion Methods
}
